import { Box, Typography } from '@mui/material';

import { COLORS } from '../../../IndividualPlan/tokens';
import { buildEnrollmentBarData, type EnrollmentBarRow } from './enrollmentBarData';
import { shortSchoolYearLabel } from '../../utils/schoolYear';
import type { PeEnrollmentYear } from '../../types';

const CONTINUING_COLOR = '#2A2A8B';
const NEWLY_IDENTIFIED_COLOR = '#6262D0';
const EXITED_COLOR = '#99DB8A';
const EXITED_LEGEND_COLOR = '#BBE7B1';
const MAX_BAR_HEIGHT = 153;
const BAR_WIDTH = 56;

const LEGEND_ITEMS = [
  { label: 'Continuing', color: CONTINUING_COLOR },
  { label: 'Newly identified', color: NEWLY_IDENTIFIED_COLOR },
  { label: 'Departed', color: EXITED_LEGEND_COLOR },
];

export interface EnrollmentBarProps {
  years: PeEnrollmentYear[];
}

function BarStack({ row }: { row: EnrollmentBarRow }) {
  if (row.continuing === null || row.newlyIdentified === null || row.exited === null) {
    return (
      <Box
        data-segment="continuing"
        aria-label={`EL students: ${row.elCount}`}
        style={{ flexGrow: 1, flexBasis: 0, backgroundColor: CONTINUING_COLOR }}
      />
    );
  }

  const segments = [
    { key: 'exited', label: 'Departed', value: row.exited, color: EXITED_COLOR },
    {
      key: 'newly-identified',
      label: 'Newly identified',
      value: row.newlyIdentified,
      color: NEWLY_IDENTIFIED_COLOR,
    },
    { key: 'continuing', label: 'Continuing', value: row.continuing, color: CONTINUING_COLOR },
  ];

  return (
    <>
      {segments.map(({ key, label, value, color }) =>
        value > 0 ? (
          <Box
            key={key}
            data-segment={key}
            aria-label={`${label}: ${value}`}
            style={{ flexGrow: value, flexBasis: 0, backgroundColor: color }}
          />
        ) : null
      )}
    </>
  );
}

export function EnrollmentBar({ years }: EnrollmentBarProps) {
  const rows = buildEnrollmentBarData(years);

  if (rows.length === 0) {
    return <Box data-testid="enrollment-bar-empty" />;
  }

  const maxCount = Math.max(...rows.map((row) => row.elCount), 1);
  const hasUnsplitYears = rows.some((row) => row.continuing === null);

  return (
    <Box data-testid="enrollment-bar" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography
          component="h4"
          sx={{ color: COLORS.muted, fontSize: 16, lineHeight: '24px', fontWeight: 700 }}
        >
          Enrollment Inflow/Outflow
        </Typography>
        <Box style={{ flexWrap: 'wrap' }} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {LEGEND_ITEMS.map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box
                style={{ backgroundColor: color }}
                sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0 }}
              />
              <Typography sx={{ fontSize: 16, lineHeight: '24px', color: COLORS.ink }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ position: 'relative', width: 'fit-content', minWidth: '100%' }}>
          <Box
            data-testid="enrollment-baseline"
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 24,
              height: '1px',
              backgroundColor: COLORS.divider,
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 8 }}>
            {rows.map((row) => (
              <Box
                key={row.schoolYear}
                data-enrollment-bar-column=""
                data-school-year={row.schoolYear}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1,
                  width: `${BAR_WIDTH}px`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: '16px',
                    fontWeight: 700,
                    textAlign: 'center',
                    color: COLORS.ink,
                  }}
                >
                  {row.elCount}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column' }}
                  style={{ height: `${(row.elCount / maxCount) * MAX_BAR_HEIGHT}px` }}
                >
                  <BarStack row={row} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: '16px',
                    textAlign: 'center',
                    color: COLORS.muted,
                  }}
                >
                  {shortSchoolYearLabel(row.schoolYear)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {hasUnsplitYears && (
        <Typography
          data-testid="enrollment-unsplit-note"
          sx={{ fontSize: 12, lineHeight: '16px', color: COLORS.muted }}
        >
          Years without inflow/outflow detail show total EL students.
        </Typography>
      )}
    </Box>
  );
}
